import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../Config";
import { toast } from "react-toastify";

const AddSubscription = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        title: "",
        price: "",
        description: "",
        duration: "",
    });

    const [errors, setErrors] = useState({
        title: "",
        price: "",
        description: "",
        duration: "",
    });

    const [submitting, setSubmitting] = useState(false);

    const validateField = (name, value) => {
        const v = typeof value === "string" ? value.trim() : value;

        switch (name) {
            case "title":
                if (!v) return "Title is required.";
                if (v.length < 3) return "Title must be at least 3 characters.";
                if (v.length > 60) return "Title must be at most 60 characters.";
                return "";

            case "price": {
                if (v === "") return "Price is required.";

                const num = Number(v);

                if (Number.isNaN(num)) return "Invalid price format.";
                if (num <= 0) return "Price must be greater than 0.";
                if (num > 100000) return "Maximum price allowed is 100000.";

                if (!/^\d+(\.\d{1,2})?$/.test(v)) {
                    return "Only 2 decimal places allowed.";
                }

                return "";
            }

            case "duration": {
                if (v === "") return "Duration is required.";
                const num = Number(v);
                if (Number.isNaN(num)) return "Duration must be a number.";
                if (!Number.isInteger(num)) return "Duration must be an integer.";
                if (num <= 0) return "Duration must be greater than 0.";
                return "";
            }

            case "description":
                if (!v) return "Description is required.";
                if (v.length < 10) return "Description must be at least 10 characters.";
                return "";

            default:
                return "";
        }
    };

    const handlePriceChange = (e) => {
        let value = e.target.value;

        if (!/^\d*\.?\d*$/.test(value)) return;

        const parts = value.split(".");
        if (parts.length > 2) return;

        if (parts[1]?.length > 2) return;

        if (Number(value) > 100000) {
            value = "100000";
        }

        setForm((prev) => ({ ...prev, price: value }));

        setErrors((prev) => ({
            ...prev,
            price: validateField("price", value),
        }));
    };
  
    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({ ...prev, [name]: value }));

        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, value),
        }));
    };

    const validateAll = () => {
        const nextErrors = {
            title: validateField("title", form.title),
            price: validateField("price", form.price),
            description: validateField("description", form.description),
            duration: validateField("duration", form.duration),
        };

        setErrors(nextErrors);

        return Object.values(nextErrors).every((err) => !err);
    };

    const isFormValid = useMemo(() => {
        return (
            form.title.trim() &&
            form.price !== "" &&
            form.description.trim() &&
            form.duration !== "" &&
            Object.values(errors).every((e) => !e)
        );
    }, [form, errors]);


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateAll()) return;

        try {
            setSubmitting(true);

            const payload = {
                title: form.title.trim(),
                price: Number(form.price),
                description: form.description.trim(),
                duration: Number(form.duration),
            };

            const response = await axiosInstance.post("/addSubscription", payload);

            if (response.data.success) {
                toast.success("Subscription added successfully");
                navigate("/subscriptionlist"); 
            } else {
                toast.error(response.data.message || "Failed to add subscription");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to add subscription.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div id="layout-wrapper">
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="title-box mb-3 pb-1">
                            <h4 className="mb-0 page-title">Add Subscription</h4>
                            <nav aria-label="breadcrumb" className="mt-1">
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard" className="new">
                                            <i className="ri-home-4-fill me-1" /> Home
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="/subscriptionlist" className="new">
                                            Subscription
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active">Add Subscription</li>
                                </ol>
                            </nav>
                        </div>

                        <div className="row justify-content-center">
                            <div className="col-lg-12">
                                <div className="card">
                                    <div className="card-body">
                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <label className="mb-1 fw-medium">Title</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.title ? "is-invalid" : ""}`}
                                                    name="title"
                                                    value={form.title}
                                                    onChange={handleChange}
                                                    placeholder="Enter subscription title"
                                                />
                                                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                                            </div>

                                            <div className="mb-3">
                                                <label className="mb-1 fw-medium">Price (in $)</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.price ? "is-invalid" : ""}`}
                                                    name="price"
                                                    value={form.price}
                                                    onChange={(e) => handlePriceChange(e)}
                                                    placeholder="Enter price"
                                                />
                                                {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                                            </div>

                                            <div className="mb-3">
                                                <label className="mb-1 fw-medium">Duration (Days)</label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${errors.duration ? "is-invalid" : ""}`}
                                                    name="duration"
                                                    value={form.duration}
                                                    onChange={handleChange}
                                                    placeholder="Enter duration in days"
                                                />
                                                {errors.duration && (
                                                    <div className="invalid-feedback">{errors.duration}</div>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <label className="mb-1 fw-medium">Description</label>
                                                <textarea
                                                    className={`form-control ${errors.description ? "is-invalid" : ""}`}
                                                    name="description"
                                                    value={form.description}
                                                    onChange={handleChange}
                                                    placeholder="Enter description"
                                                    rows={4}
                                                />
                                                {errors.description && (
                                                    <div className="invalid-feedback">{errors.description}</div>
                                                )}
                                            </div>

                                            <div className="text-end mb-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary me-2 px-4"
                                                    onClick={() => navigate("/subscriptionlist")}
                                                    disabled={submitting}
                                                >
                                                    Cancel
                                                </button>

                                                <button
                                                    type="submit"
                                                    className="btn btn-danger px-4"
                                                    disabled={!isFormValid || submitting}
                                                >
                                                    {submitting ? "Adding..." : "Add Subscription"}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddSubscription;